import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { LIVE_BOT_LEVELS, type LiveBotLevel } from '../../sim/bot/seats.ts';
import {
  BOT_KNOB_LIMITS,
  botTraits,
  consistencyRange,
  DRAGGABLE_TRAIT_KEYS,
  effectiveDials,
  estimatedPace,
  MIN_SKILL_DIAL,
  presetProfileKnobs,
  profileTraits,
  READOUT_TRAIT_KEYS,
  type BotDials,
  type BotProfileDocument,
  type BotProfileFlagKey,
  type BotProfileNumberKey,
  type BotTraits,
} from '../../sim/botProfile/index.ts';
import type { PaceReport, PlaytestKind, PlaytestSketch, RacecraftReport } from '../../sim/bot/index.ts';
import { BoatNameField } from '../boats/nameField.tsx';
import { EditorIcon, RedoIcon, UndoIcon } from '../editor/icons.tsx';
import { ScrubNumber } from '../scrubNumber.tsx';
import { Tooltip } from '../tooltip.tsx';
import { setPanel } from '../panels.ts';
import { PlaytestHistory } from './playtestHistory.tsx';
import { PackLog } from './packResult.tsx';
import { PlaytestLog } from './playtestLog.tsx';
import type { UiToastTone } from '../toast.ts';
import '../editor/toolbar.css';
import './workshop.css';

const MESSAGE_TIMEOUT_MS = 4000;

export type BotCatalogChoice = { id: string; label: string };

/**
 * One finished playtest, kept for the session so tests can be compared and restored: a solo
 * quick playtest (pace) or a pack playtest against Expert (racecraft).
 */
export type PlaytestHistoryEntry = {
  id: number;
  /** Shown as #1, #2, … in the order they ran. */
  number: number;
  finishedAt: number;
  mapId: string;
  sketch: PlaytestSketch;
  /** The bot as it raced: knobs, bars, preset. */
  profile: BotProfileDocument;
} & ({ kind: 'solo'; report: PaceReport } | { kind: 'pack'; report: RacecraftReport });

/** A quick playtest's state: solo races on one map, measured against Expert's time there. */
export type BotPlaytestState = {
  maps: readonly BotCatalogChoice[];
  /** The map the chart button races on: the last one picked from Test. */
  mapId: string | null;
  /** The map the current or last run raced on. */
  raceMapId: string | null;
  status: 'idle' | 'running' | 'done' | 'error';
  /** The running test's kind, or the shown one's. */
  kind: PlaytestKind;
  done: number;
  total: number;
  /** Share of the running playtest done, 0–1. */
  fraction: number;
  /** Estimated seconds left while running, or null until there is enough to go on. */
  secondsLeft: number | null;
  /** Measured share of Expert's time, once done. */
  pace: number | null;
  /** Slowest and fastest single race, as shares of Expert's time. */
  paceRange: readonly [number, number] | null;
  races: number;
  finished: number;
  crashes: number;
  /** The knobs changed since this result was measured. */
  stale: boolean;
  error: string | null;
  /** The finished run, for the log. */
  report: PaceReport | null;
  /** The shown pack test, for the pack log. */
  pack: RacecraftReport | null;
  /** The map the report raced on, drawn under the tracks. */
  sketch: PlaytestSketch | null;
  /** Every finished test of this bot this session, newest first. */
  history: readonly PlaytestHistoryEntry[];
  /** The test the result row and log show. */
  selectedId: number | null;
};

export type BotWorkshopState = {
  profile: BotProfileDocument;
  /** `bots/<id>.json`, or null until the first save. */
  fileName: string | null;
  bots: readonly BotCatalogChoice[];
  currentBotId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  canDelete: boolean;
  dirty: boolean;
  playtest: BotPlaytestState;
  message: string | null;
  messageTone: UiToastTone;
};

export type BotWorkshopActions = {
  onNew: () => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
  onOpenBot: (id: string) => void;
  onPreset: (level: LiveBotLevel) => void;
  onNumber: (key: BotProfileNumberKey, value: number) => void;
  onFlag: (key: BotProfileFlagKey, value: boolean) => void;
  onCourseSense: (value: 'time' | 'distance') => void;
  onRename: (raw: string) => void;
  /** A dragged bar: `move` while dragging, `end` on release (and on every key step). */
  onDial: (key: keyof BotDials, value: number, phase: 'move' | 'end') => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Runs a quick playtest; with an id, on that map (and remembers it). */
  onPlaytest: (mapId?: string) => void;
  /** Runs a pack playtest against Expert on the picked map. */
  onPackPlaytest: () => void;
  onCancelPlaytest: () => void;
  /** Picks the map the next playtest races on, without starting it. */
  onPlaytestMap: (mapId: string) => void;
  /** Shows an earlier test's result and log. */
  onSelectRun: (id: number) => void;
  /** Puts the bot back as that test raced it. */
  onRestoreRun: (id: number) => void;
  onClearRuns: () => void;
};

export type BotWorkshopProps = {
  state: BotWorkshopState;
  actions: BotWorkshopActions;
};

type NumberKnob = { key: BotProfileNumberKey; label: string; hint: string };
type FlagKnob = { key: BotProfileFlagKey; label: string; hint: string };

const LEVEL_LABEL: Readonly<Record<LiveBotLevel, string>> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  expert: 'Expert',
};

const SKILL: readonly NumberKnob[] = [
  { key: 'depth', label: 'Foresight', hint: 'Turns it plans ahead before committing a click.' },
];
const PRECISION: readonly NumberKnob[] = [
  { key: 'temperature', label: 'Slips', hint: 'Turns lost it shrugs off when choosing a click. 0 always takes the best.' },
  { key: 'aimErrorM', label: 'Aim error (m)', hint: 'How far off the click can land, more at speed.' },
];
const RISK: readonly NumberKnob[] = [
  {
    key: 'brakeConfidence',
    label: 'Brake confidence',
    hint: 'Above 1 it trusts its brakes too much and arrives hot. Below 1 it backs off early.',
  },
  {
    key: 'speedLimitKmh',
    label: 'Speed limit (km/h)',
    hint: 'The fastest it lets itself sail. Slow and clean: it never crashes for it. 0 has no limit.',
  },
];
const RACECRAFT_FLAGS: readonly FlagKnob[] = [
  { key: 'nudges', label: 'Squeezes', hint: 'Allows free side rubs and tries clicks that brush a boat.' },
  { key: 'followDistance', label: 'Following distance', hint: 'Keeps clear of where a rival would stop if it braked hard.' },
  { key: 'forecastHulls', label: 'Reads rivals', hint: 'Keeps clear of where each rival is forecast to park next.' },
];
const RACECRAFT_NUMBERS: readonly NumberKnob[] = [
  { key: 'contactRoots', label: 'Squeeze tries', hint: 'Brushing clicks checked against the real collision.' },
  { key: 'aggression', label: 'Blocking', hint: 'Rival turns one of its own turns is worth giving up to block. 0 never blocks.' },
  { key: 'blockMaxCostTurns', label: 'Block max cost', hint: 'The most a block may cost it, in turns.' },
  { key: 'blockMinGainTurns', label: 'Block min gain', hint: 'The least a block must hold a rival up, in turns.' },
];
const ADVANCED: readonly NumberKnob[] = [
  { key: 'width', label: 'Lines kept', hint: 'Lines kept per turn of lookahead.' },
  { key: 'density', label: 'Samples', hint: 'Reach samples per line.' },
  { key: 'rootDensity', label: 'First-click samples', hint: 'Reach samples for the click being chosen.' },
  { key: 'verify', label: 'Exact checks', hint: 'Ranked clicks checked against the real rules.' },
];

/** Label for every knob, for the history's "changed since" line. */
const KNOB_LABEL: Readonly<Record<string, string>> = Object.fromEntries([
  ...[...SKILL, ...PRECISION, ...RISK, ...RACECRAFT_FLAGS, ...RACECRAFT_NUMBERS, ...ADVANCED].map((knob) => [
    knob.key,
    knob.label,
  ]),
  ['courseSense', 'Course sense'],
]);

const MAX_CHANGES_LISTED = 4;

/**
 * What differs between the bot a test raced and the bot now. Bars when both have them (that is
 * what the player moved); otherwise knob by knob, with values.
 */
function profileChanges(from: BotProfileDocument, to: BotProfileDocument): string[] {
  const changes: string[] = [];

  if (from.dials && to.dials) {
    const a = effectiveDials(from.dials);
    const b = effectiveDials(to.dials);

    for (const key of DRAGGABLE_TRAIT_KEYS) {
      if (a[key] !== b[key]) {
        changes.push(`${TRAIT_LABEL[key]} ${a[key]}→${b[key]}`);
      }
    }
  } else if ((from.dials === null) !== (to.dials === null)) {
    changes.push(to.dials === null ? 'now Custom' : 'now on the bars');
  }

  if (changes.length === 0 || from.dials === null || to.dials === null) {
    const show = (value: unknown) => (typeof value === 'boolean' ? (value ? 'on' : 'off') : String(value));

    for (const key of Object.keys(to.knobs) as (keyof BotProfileDocument['knobs'])[]) {
      if (from.knobs[key] !== to.knobs[key]) {
        changes.push(`${KNOB_LABEL[key] ?? key} ${show(from.knobs[key])}→${show(to.knobs[key])}`);
      }
    }
  }

  return changes.length > MAX_CHANGES_LISTED
    ? [...changes.slice(0, MAX_CHANGES_LISTED), `+${changes.length - MAX_CHANGES_LISTED} more`]
    : changes;
}

const TRAIT_LABEL: Readonly<Record<keyof BotTraits, string>> = {
  skill: 'Skill',
  foresight: 'Foresight',
  precision: 'Precision',
  aggression: 'Aggression',
  manners: 'Manners',
  consistency: 'Consistency',
};

const TRAIT_HINT: Readonly<Record<keyof BotTraits, string>> = {
  skill: 'The pace it aims for: 100 is Expert, 60 is 140% of Expert’s time.',
  consistency:
    'How alike one race is to the next. High: it loses its time steadily (small slips, not seeing bends early, and on slower bots a speed limit), with no crashes. Low: by aim error, which crashes and scatters races.',
  aggression: 'Squeezing past, squeeze tries, and, above the middle, blocking.',
  manners: 'Reading rivals and keeping a following distance; holds blocking back.',
  foresight: 'Follows from Consistency: whether it sees bends coming (steady bots lose time here).',
  precision: 'Follows from Skill and Consistency: few slips, little aim error.',
};

type BotMenu =
  | 'file'
  | 'bots'
  | 'preset'
  | 'skill'
  | 'precision'
  | 'risk'
  | 'racecraft'
  | 'advanced'
  | 'test';

function MenuItem({
  label,
  shortcut,
  disabled,
  pressed,
  onClick,
}: {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      class="editor-menu-item"
      role="menuitem"
      disabled={disabled}
      aria-pressed={pressed === undefined ? undefined : pressed ? 'true' : 'false'}
      onClick={onClick}
    >
      <span>{label}</span>
      {shortcut ? <span class="editor-toolbar-shortcut">{shortcut}</span> : null}
    </button>
  );
}

function NumberKnobField({
  knob,
  value,
  onChange,
}: {
  knob: NumberKnob;
  value: number;
  onChange: (next: number) => void;
}): JSX.Element {
  const limit = BOT_KNOB_LIMITS[knob.key];

  return (
    <div class="bot-knob" data-bot-knob={knob.key}>
      <ScrubNumber
        label={knob.label}
        value={value}
        min={limit.min}
        max={limit.max}
        step={limit.step}
        openOnFocus={true}
        showEnds={true}
        onChange={onChange}
      />
      <p class="ui-hint">{knob.hint}</p>
    </div>
  );
}

function FlagKnobField({
  knob,
  value,
  onChange,
}: {
  knob: FlagKnob;
  value: boolean;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <div class="bot-knob" data-bot-knob={knob.key}>
      <button type="button" class="ui-button" aria-pressed={value} onClick={() => onChange(!value)}>
        {knob.label}: {value ? 'On' : 'Off'}
      </button>
      <p class="ui-hint">{knob.hint}</p>
    </div>
  );
}

/**
 * The bot's six traits as a bar chart, each bar its own colour. A tick on each bar marks the
 * preset the bot started from, so an edit shows how far it moved.
 */
function formatSecondsLeft(seconds: number): string {
  if (seconds < 1.5) {
    return 'a second';
  }

  if (seconds < 60) {
    return `${Math.round(seconds)} s`;
  }

  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${Math.round(seconds - minutes * 60)} s`;
}

function PlaytestRow({
  playtest,
  actions,
}: {
  playtest: BotPlaytestState;
  actions: BotWorkshopActions;
}): JSX.Element {
  const labelOf = (id: string | null | undefined) =>
    playtest.maps.find((entry) => entry.id === id)?.label ?? id ?? 'no map';
  const mapLabel = labelOf(playtest.mapId);
  // The run names the map it raced on, even after the picker moves on.
  const measuredLabel = labelOf(playtest.raceMapId);
  const running = playtest.status === 'running';
  let text: JSX.Element | string;

  if (running) {
    const percent = Math.floor(playtest.fraction * 100);
    const left = playtest.secondsLeft === null ? 'estimating time…' : `about ${formatSecondsLeft(playtest.secondsLeft)} left`;
    text = (
      <>
        {playtest.kind === 'pack' ? 'Pack racing' : 'Racing'} on {measuredLabel}: <strong>{percent}%</strong>
        <span class="ui-hint">
          {' '}
          · {left} · race {Math.min(playtest.done + 1, playtest.total || 1)} of {playtest.total || '…'}
        </span>
        <progress class="bot-playtest-progress" max={1} value={playtest.fraction} aria-label="Playtest progress" />
      </>
    );
  } else if (playtest.status === 'error') {
    text = `Playtest failed: ${playtest.error ?? 'unknown error'}`;
  } else {
    // Results live in the history beside the chart; the row only starts runs.
    text = (
      <span class="ui-hint">
        Quick playtest races this bot solo on {mapLabel} against Expert’s time; Pack playtest races it against
        Expert there in both seat orders and scores its racecraft. Results land in the playtest history.
      </span>
    );
  }

  return (
    <div
      class="bot-playtest"
      data-playtest={playtest.status}
      data-playtest-kind={playtest.kind}
      data-playtest-pace={playtest.pace === null ? undefined : playtest.pace.toFixed(3)}
      data-playtest-stale={playtest.stale ? 'true' : undefined}
    >
      <p class="bot-playtest-text" role="status">
        {text}
      </p>
      <label class="bot-playtest-map">
        <span class="ui-hint">Map</span>
        <select
          class="ui-select"
          aria-label="Playtest map"
          data-bot-playtest-map
          value={playtest.mapId ?? ''}
          disabled={running || playtest.maps.length === 0}
          onChange={(event) => actions.onPlaytestMap(event.currentTarget.value)}
        >
          {playtest.maps.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      {running ? (
        <button type="button" class="ui-button" data-bot-playtest-cancel onClick={actions.onCancelPlaytest}>
          Cancel
        </button>
      ) : (
        <>
          <button
            type="button"
            class="ui-button"
            data-bot-playtest
            disabled={playtest.mapId === null}
            onClick={() => actions.onPlaytest()}
          >
            Quick playtest
          </button>
          <button
            type="button"
            class="ui-button"
            data-bot-pack-playtest
            title="Race against Expert, moving first and moving last, and score squeezes, blocks, hits, and room"
            disabled={playtest.mapId === null}
            onClick={actions.onPackPlaytest}
          >
            Pack playtest
          </button>
        </>
      )}
      {playtest.status === 'done' && playtest.report && playtest.sketch ? (
        <PlaytestLog report={playtest.report} sketch={playtest.sketch} />
      ) : null}
      {playtest.status === 'done' && playtest.pack ? <PackLog report={playtest.pack} /> : null}
    </div>
  );
}

const DIAL_STEP_COARSE = 10;

/** One bar a player drags (or steps with the keys). The shaded band is the reachable range. */
function DialBar({
  dial,
  value,
  preset,
  range,
  presetLabel,
  onDial,
}: {
  dial: keyof BotDials;
  value: number;
  preset: number;
  range: readonly [number, number];
  presetLabel: string;
  onDial: BotWorkshopActions['onDial'];
}): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const valueAt = (clientY: number): number => {
    const box = trackRef.current?.getBoundingClientRect();

    if (!box || box.height === 0) {
      return value;
    }

    return Math.round(Math.min(1, Math.max(0, (box.bottom - clientY) / box.height)) * 100);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const step = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1, PageUp: DIAL_STEP_COARSE, PageDown: -DIAL_STEP_COARSE }[
      event.key
    ];
    const next =
      step !== undefined ? value + step : event.key === 'Home' ? range[0] : event.key === 'End' ? range[1] : null;

    if (next === null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onDial(dial, next, 'end');
  };

  return (
    <div class="bot-trait is-draggable" role="listitem" data-trait={dial} data-value={value} title={TRAIT_HINT[dial]}>
      <span class="bot-trait-value">{value}</span>
      <div
        ref={trackRef}
        class="bot-trait-track"
        role="slider"
        tabIndex={0}
        aria-label={TRAIT_LABEL[dial]}
        aria-valuemin={range[0]}
        aria-valuemax={range[1]}
        aria-valuenow={value}
        aria-orientation="vertical"
        onPointerDown={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
          (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
          dragging.current = true;
          onDial(dial, valueAt(event.clientY), 'move');
        }}
        onPointerMove={(event) => {
          if (dragging.current) {
            onDial(dial, valueAt(event.clientY), 'move');
          }
        }}
        onPointerUp={(event) => {
          if (dragging.current) {
            dragging.current = false;
            onDial(dial, valueAt(event.clientY), 'end');
          }
        }}
        onPointerCancel={() => {
          if (dragging.current) {
            dragging.current = false;
            onDial(dial, value, 'end');
          }
        }}
        onKeyDown={onKeyDown}
      >
        {range[0] > 0 || range[1] < 100 ? (
          <div class="bot-trait-band" style={{ bottom: `${range[0]}%`, height: `${range[1] - range[0]}%` }} />
        ) : null}
        <div class="bot-trait-bar" style={{ height: `${value}%` }}>
          <span class="bot-trait-grip" />
        </div>
        <div class="bot-trait-preset" style={{ bottom: `${preset}%` }} aria-label={`${presetLabel}: ${preset}`} />
      </div>
      <span class="bot-trait-label">{TRAIT_LABEL[dial]}</span>
    </div>
  );
}

/** A bar that only follows from the others: no handle, no drag. */
function ReadoutBar({
  trait,
  value,
  preset,
  presetLabel,
}: {
  trait: keyof BotTraits;
  value: number;
  preset: number;
  presetLabel: string;
}): JSX.Element {
  return (
    <div class="bot-trait is-readout" role="listitem" data-trait={trait} data-value={value} title={TRAIT_HINT[trait]}>
      <span class="bot-trait-value">{value}</span>
      <div class="bot-trait-track" role="meter" aria-label={TRAIT_LABEL[trait]} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}>
        <div class="bot-trait-bar" style={{ height: `${value}%` }} />
        <div class="bot-trait-preset" style={{ bottom: `${preset}%` }} aria-label={`${presetLabel}: ${preset}`} />
      </div>
      <span class="bot-trait-label">{TRAIT_LABEL[trait]}</span>
    </div>
  );
}

/**
 * The bot's traits, each bar its own colour. The four on the left are the simple way to build a
 * bot: drag them. The two on the right only follow from them. A tick on each bar marks the
 * preset the bot started from.
 */
function TraitChart({
  profile,
  onDial,
  children,
}: {
  profile: BotProfileDocument;
  onDial: BotWorkshopActions['onDial'];
  children?: JSX.Element;
}): JSX.Element {
  const traits = profileTraits(profile);
  const preset = botTraits(presetProfileKnobs(profile.basedOn));
  const pace = estimatedPace(profile.knobs);
  const presetLabel = LEVEL_LABEL[profile.basedOn];
  const custom = profile.dials === null;
  const ranges: Readonly<Record<keyof BotDials, readonly [number, number]>> = {
    skill: [MIN_SKILL_DIAL, 100],
    consistency: consistencyRange(traits.skill),
    aggression: [0, 100],
    manners: [0, 100],
  };

  return (
    <section class="bot-traits ui-panel ui-chrome" aria-label="Traits" data-bot-custom={custom ? 'true' : 'false'}>
      <div class="bot-traits-chart">
        <div class="bot-traits-group is-draggable">
          <p class="bot-traits-group-title">Drag to shape</p>
          <div class="bot-traits-bars" role="list" aria-label="Draggable traits">
            {DRAGGABLE_TRAIT_KEYS.map((key) => (
              <DialBar
                key={key}
                dial={key}
                value={traits[key]}
                preset={preset[key]}
                range={ranges[key]}
                presetLabel={presetLabel}
                onDial={onDial}
              />
            ))}
          </div>
        </div>
        <div class="bot-traits-group is-readout">
          <p class="bot-traits-group-title">Follows from these</p>
          <div class="bot-traits-bars" role="list" aria-label="Resulting traits">
            {READOUT_TRAIT_KEYS.map((key) => (
              <ReadoutBar key={key} trait={key} value={traits[key]} preset={preset[key]} presetLabel={presetLabel} />
            ))}
          </div>
        </div>
      </div>
      {custom ? (
        <p class="bot-traits-custom" data-bot-custom-note>
          <strong>Custom:</strong> set knob by knob in the menus. The bars show where it reads; dragging one
          rebuilds every knob from the bars (search settings back to Expert’s).
        </p>
      ) : null}
      <p class="bot-traits-pace" data-pace={pace.toFixed(2)}>
        Estimated pace: <strong>{Math.round(pace * 100)}%</strong> of Expert’s time
        <span class="ui-hint"> · Ticks mark {presetLabel}</span>
      </p>
      {children}
    </section>
  );
}

/**
 * Bot workshop chrome, laid out like the boat workshop: menubar with drop-downs (File, Bots,
 * Preset, then one menu per knob group), undo / redo, the name in the middle, and the trait
 * chart in the middle of the screen. BotScene owns the profile; this only renders it.
 */
export function BotWorkshopPanel({ state, actions }: BotWorkshopProps): JSX.Element {
  const [open, setOpen] = useState<BotMenu | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const { profile } = state;
  const { knobs } = profile;

  useEffect(() => {
    if (open === null) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      // The scrub slider is portaled out of the menu; dragging it must not close the menu.
      if (barRef.current?.contains(target) || (target instanceof Element && target.closest('.ui-scrub-slider-popover'))) {
        return;
      }

      setOpen(null);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(null);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: BotMenu) => setOpen((current) => (current === id ? null : id));

  const run = (action: () => void) => {
    action();
    setOpen(null);
  };

  const menu = (id: BotMenu, label: string, body: JSX.Element, panelClass = '') => (
    <div class="editor-menu">
      <button
        type="button"
        class="editor-menu-trigger"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={open === id ? 'true' : 'false'}
        onClick={() => toggle(id)}
      >
        {label}
      </button>
      <div class={`editor-menu-panel ui-panel ${panelClass}`} role="menu" aria-label={label} hidden={open !== id}>
        {body}
      </div>
    </div>
  );

  const numbers = (list: readonly NumberKnob[]) =>
    list.map((knob) => (
      <NumberKnobField
        key={knob.key}
        knob={knob}
        value={knobs[knob.key]}
        onChange={(value) => actions.onNumber(knob.key, value)}
      />
    ));

  return (
    <div
      class="editor-toolbar"
      role="toolbar"
      aria-label="Bot workshop"
      data-bot-dirty={state.dirty ? 'true' : 'false'}
    >
      <div ref={barRef} class="editor-menubar ui-panel ui-chrome">
        <div class="editor-menubar-left">
          <div class="editor-menubar-menus" role="menubar" aria-label="Bot workshop">
            {menu(
              'file',
              'File',
              <>
                <MenuItem label="New" onClick={() => run(actions.onNew)} />
                <MenuItem label="Save" shortcut="Ctrl+S" onClick={() => run(actions.onSave)} />
                <MenuItem label="Delete" disabled={!state.canDelete} onClick={() => run(actions.onDelete)} />
                <MenuItem label="Close" onClick={() => run(actions.onClose)} />
              </>,
            )}
            {menu(
              'bots',
              'Bots',
              state.bots.length === 0 ? (
                <MenuItem label="No saved bots" disabled onClick={() => undefined} />
              ) : (
                <>
                  {state.bots.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      label={entry.label}
                      pressed={state.currentBotId === entry.id}
                      onClick={() => run(() => actions.onOpenBot(entry.id))}
                    />
                  ))}
                </>
              ),
            )}
            {menu(
              'preset',
              'Preset',
              <>
                {LIVE_BOT_LEVELS.map((level) => (
                  <MenuItem
                    key={level}
                    label={`Start from ${LEVEL_LABEL[level]}`}
                    pressed={profile.basedOn === level}
                    onClick={() => run(() => actions.onPreset(level))}
                  />
                ))}
              </>,
            )}
            {menu(
              'skill',
              'Skill',
              <>
                {numbers(SKILL)}
                <div class="bot-knob" data-bot-knob="courseSense">
                  <div class="ui-button-group" role="group" aria-label="Course sense">
                    {(['time', 'distance'] as const).map((sense) => (
                      <button
                        key={sense}
                        type="button"
                        class="ui-button"
                        aria-pressed={knobs.courseSense === sense}
                        onClick={() => actions.onCourseSense(sense)}
                      >
                        {sense === 'time' ? 'Sees bends' : 'Distance only'}
                      </button>
                    ))}
                  </div>
                  <p class="ui-hint">What it knows past its foresight: bends coming, or only how far.</p>
                </div>
              </>,
              'bot-knob-menu',
            )}
            {menu('precision', 'Precision', <>{numbers(PRECISION)}</>, 'bot-knob-menu')}
            {menu('risk', 'Risk', <>{numbers(RISK)}</>, 'bot-knob-menu')}
            {menu(
              'racecraft',
              'Racecraft',
              <>
                {RACECRAFT_FLAGS.map((knob) => (
                  <FlagKnobField
                    key={knob.key}
                    knob={knob}
                    value={knobs[knob.key]}
                    onChange={(value) => actions.onFlag(knob.key, value)}
                  />
                ))}
                {numbers(RACECRAFT_NUMBERS)}
              </>,
              'bot-knob-menu',
            )}
            {menu('advanced', 'Advanced', <>{numbers(ADVANCED)}</>, 'bot-knob-menu')}
            {menu(
              'test',
              'Test',
              <>
                {state.playtest.status === 'running' ? (
                  <MenuItem label="Cancel playtest" onClick={() => run(actions.onCancelPlaytest)} />
                ) : null}
                {state.playtest.status !== 'running' && state.playtest.mapId !== null ? (
                  <MenuItem
                    label={`Pack playtest on ${
                      state.playtest.maps.find((entry) => entry.id === state.playtest.mapId)?.label ?? state.playtest.mapId
                    }`}
                    onClick={() => run(actions.onPackPlaytest)}
                  />
                ) : null}
                {state.playtest.maps.length === 0 ? (
                  <MenuItem label="No maps" disabled onClick={() => undefined} />
                ) : (
                  state.playtest.maps.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      label={`Playtest on ${entry.label}`}
                      pressed={state.playtest.mapId === entry.id}
                      onClick={() => run(() => actions.onPlaytest(entry.id))}
                    />
                  ))
                )}
              </>,
            )}
          </div>
          <div class="editor-menubar-history">
            <Tooltip label="Undo" shortcut="Ctrl+Z">
              <button
                type="button"
                class="editor-rail-button"
                aria-label="Undo"
                disabled={!state.canUndo}
                onClick={actions.onUndo}
              >
                <EditorIcon icon={UndoIcon} />
              </button>
            </Tooltip>
            <Tooltip label="Redo" shortcut="Ctrl+Y">
              <button
                type="button"
                class="editor-rail-button"
                aria-label="Redo"
                disabled={!state.canRedo}
                onClick={actions.onRedo}
              >
                <EditorIcon icon={RedoIcon} />
              </button>
            </Tooltip>
          </div>
        </div>
        <div class="editor-menubar-center">
          <BoatNameField
            name={profile.name}
            fileName={state.fileName ?? ''}
            noun="bot"
            onRename={actions.onRename}
          />
        </div>
        <p class="editor-menubar-brand">Helmway Bots</p>
      </div>
      <div class="bot-workshop-body">
        <TraitChart profile={profile} onDial={actions.onDial}>
          <PlaytestRow playtest={state.playtest} actions={actions} />
        </TraitChart>
        <PlaytestHistory
          history={state.playtest.history}
          selectedId={state.playtest.selectedId}
          maps={state.playtest.maps}
          running={state.playtest.status === 'running'}
          changesOf={(entry) => profileChanges(entry.profile, profile)}
          onSelect={actions.onSelectRun}
          onRestore={actions.onRestoreRun}
          onClear={actions.onClearRuns}
        />
      </div>
      <p
        class="ui-toast editor-toolbar-message ui-chrome"
        data-tone={state.messageTone}
        role="status"
        hidden={state.message === null}
      >
        {state.message}
      </p>
    </div>
  );
}

export type BotWorkshop = {
  sync: () => void;
  showMessage: (text: string, tone: UiToastTone) => void;
  destroy: () => void;
};

/** Mounts the workshop chrome; BotScene calls `sync` after every change it owns. */
export function mountBotWorkshop(options: {
  getState: () => Omit<BotWorkshopState, 'message' | 'messageTone'>;
  actions: BotWorkshopActions;
}): BotWorkshop {
  let message: string | null = null;
  let messageTone: UiToastTone = 'success';
  let timer: number | null = null;

  const sync = () => {
    setPanel('botWorkshop', {
      state: { ...options.getState(), message, messageTone },
      actions: options.actions,
    });
  };

  sync();

  return {
    sync,
    showMessage(text, tone) {
      message = text;
      messageTone = tone;

      if (timer !== null) {
        window.clearTimeout(timer);
      }

      timer = window.setTimeout(() => {
        message = null;
        timer = null;
        sync();
      }, MESSAGE_TIMEOUT_MS);
      sync();
    },
    destroy() {
      if (timer !== null) {
        window.clearTimeout(timer);
      }

      setPanel('botWorkshop', null);
    },
  };
}
